import numbro from 'numbro';
import type {
  ContactInformationPayload,
  CustomerInquiryPayload,
  FacilityDataPayload,
  FacilityFormValues,
  InformationFormValues,
  InformationPayload,
  IndividualOfferFormValues,
  OfferGenerationWizardFormValues,
} from '../../../domain/customerInquiry';
import {
  BUSINESS_CHARGING_BOOLEAN_KEYS,
  FACILITY_TYPE_2_FACILITY_DATA_KEY,
  FACILITY_TYPE_2_NUMBER_FIELDS,
  type FacilityDataKey,
  type FacilityType,
} from './customerInquiryPayload.config';

export function unformatNumbers(
  obj: Record<string, unknown>,
  keys: string[]
): Record<string, string | null> {
  const o = { ...obj } as Record<string, string | null>;
  keys.forEach((k) => {
    const value = o[k];
    if (value && typeof value === 'string' && value.length > 0) {
      // TODO: send the plain number if the middleware allows it
      o[k] = String(numbro.unformat(value));
    } else {
      o[k] = null;
    }
  });
  return o;
}

function convertStringToBoolean(string: string): boolean | null {
  if (string === 'true') {
    return true;
  }
  if (string === 'false') {
    return false;
  }
  return null;
}

function isFacilityType(value: string | undefined): value is FacilityType {
  return value !== undefined && value in FACILITY_TYPE_2_FACILITY_DATA_KEY;
}

export function prepareFacilityData(
  facilityType: string | undefined,
  values: OfferGenerationWizardFormValues
): FacilityDataPayload[] | null {
  if (!isFacilityType(facilityType)) {
    return null;
  }
  const facilityKey: FacilityDataKey =
    FACILITY_TYPE_2_FACILITY_DATA_KEY[facilityType];
  const numberFields = FACILITY_TYPE_2_NUMBER_FIELDS[facilityType];
  const facilities = values[facilityKey];
  if (!facilities) {
    return null;
  }

  return facilities.map((facility: FacilityFormValues) => {
    const { id: _id, gridOperator, country, ...rest } = facility;
    const payload: FacilityDataPayload = {};
    Object.entries(rest).forEach(([key, value]) => {
      if (typeof value === 'string' || value === null || value === undefined) {
        payload[key] = value ?? null;
      }
    });
    if (gridOperator) {
      payload.gridOperator = {
        mastrNumber: gridOperator.id,
        name: gridOperator.name,
      };
    }
    if (country) {
      payload.country = country.id;
    }
    return unformatNumbers(payload, numberFields);
  });
}

export function prepareInformationData(
  information: InformationFormValues,
  removeDates: boolean
): InformationPayload {
  const { country, ...rest } = information;
  delete rest.file;
  const result = {
    ...rest,
    country: country.id,
  } as InformationPayload;

  if (removeDates) {
    delete result.start;
    delete result.end;
  }
  return result;
}

export function prepareContactData(
  contactData: IndividualOfferFormValues
): ContactInformationPayload {
  const { salutation } = contactData;
  let salutationForMiddleware: string | null;
  if (
    salutation === undefined ||
    salutation === null ||
    salutation.id === 'notSpecified'
  ) {
    salutationForMiddleware = null;
  } else {
    salutationForMiddleware = salutation.id;
  }

  return {
    ...contactData,
    salutation: salutationForMiddleware,
  };
}

export function buildCustomerInquiryPayload(
  values: OfferGenerationWizardFormValues
): CustomerInquiryPayload {
  const removeDates =
    values.product === 'pvGrossanlage' || values.product === 'businessCharging';

  const payload: CustomerInquiryPayload = {
    category: values.category,
    product: values.product,
    facilityType: values.facilityType,
    facilityData: prepareFacilityData(values.facilityType, values),
    information: prepareInformationData(values.information, removeDates),
    contactInformation: prepareContactData(values.individualOffer),
  };

  switch (values.product) {
    case 'zertifikate': {
      payload.certificate = values.certificate;
      switch (values.certificate) {
        case 'co2': {
          payload.co2CertificateData = unformatNumbers(
            values.co2CertificateData ?? {},
            ['consumption']
          );
          break;
        }
        case 'proofOfOrigin': {
          payload.proofOfOriginCertificateData = unformatNumbers(
            values.proofOfOriginCertificateData ?? {},
            ['consumption']
          );
          break;
        }
        default:
          break;
      }
      break;
    }
    case 'pvGrossanlage': {
      payload.largeSolarFacilityData = unformatNumbers(
        {
          location: values.largeSolarFacilityType,
          sizeOfArea: values.largeSolarFacilityData?.sizeOfArea,
          consumption: values.largeSolarFacilityData?.consumption,
        },
        ['sizeOfArea', 'consumption']
      );
      break;
    }
    case 'stromlieferung': {
      payload.powerType = values.powerType;
      if (values.powerType === 'gruenstrom' && values.greenPowerData) {
        payload.greenPowerData = unformatNumbers(
          values.greenPowerData as Record<string, unknown>,
          ['consumption']
        );
      }
      if (values.powerType === 'graustrom' && values.greyPowerData) {
        payload.greyPowerData = unformatNumbers(values.greyPowerData, [
          'consumption',
        ]);
      }
      break;
    }
    case 'businessCharging': {
      payload.businessChargingData = unformatNumbers(
        values.businessChargingData ?? {},
        [
          'numberOfEmployees',
          'numberOfParkingSpaces',
          'numberOfOwnVehicles',
          'numberOfElectricCars',
        ]
      );

      BUSINESS_CHARGING_BOOLEAN_KEYS.forEach((key) => {
        const chargingData = payload.businessChargingData;
        if (!chargingData) {
          return;
        }
        const raw = chargingData[key];
        if (typeof raw === 'string') {
          chargingData[key] = convertStringToBoolean(raw);
        }
      });
      break;
    }
    case 'immoCharging': {
      payload.product =
        values.immoChargingData?.isResidentialOrCommercial === 'residential'
          ? 'immoChargingResidential'
          : 'immoChargingCommercial';

      payload.immoChargingData = unformatNumbers(
        {
          product:
            values.immoChargingData?.isResidentialOrCommercial === 'residential'
              ? 'immoChargingResidential'
              : 'immoChargingCommercial',
          numberOfUnits: values.immoChargingData?.numberOfUnits,
          numberOfGarageObjects: values.immoChargingData?.numberOfGarageObjects,
          numberOfParkingSpaces: values.immoChargingData?.numberOfParkingSpaces,
        },
        ['numberOfUnits', 'numberOfGarageObjects', 'numberOfParkingSpaces']
      );
      break;
    }
    case 'coopCharging': {
      payload.coopChargingData = unformatNumbers(
        values.coopChargingData ?? {},
        ['numberOfHotelLocations', 'numberOfRooms', 'numberOfParkingSpaces']
      );
      break;
    }
    default:
      break;
  }

  return payload;
}

export function buildCustomerInquiryFormData(
  values: OfferGenerationWizardFormValues
): FormData {
  const formData = new FormData();
  formData.append(
    'customerInquiry',
    JSON.stringify(buildCustomerInquiryPayload(values))
  );
  if (values.information.file) {
    formData.append('additionalInformationFile', values.information.file);
  }
  if (values.windFacilityFile) {
    formData.append('windFacilityFile', values.windFacilityFile);
  }
  return formData;
}
